import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { exportToExcel } from '@/utils/exportUtils';
import { scrapApi, SCRAP_SOURCE_LABEL, SCRAP_REASON_LABEL, type ScrapReport, type ScrapGroup, type ScrapSource, type ScrapReason } from '@/api/scrap.api';
import { inr, qtyText, dateText } from '@/pages/manufacturing/scrap/components/scrapUi';

// Scrap reports: how much scrap, where it came from (source, order, stage, item, reason), what it cost
// the P&L and what its sale recovered.

type Tab = 'summary' | 'orders' | 'items' | 'stages' | 'reasons' | 'recovery';

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: 'summary', label: 'Summary', icon: 'ri-pie-chart-line' },
  { key: 'orders', label: 'By Production Order', icon: 'ri-file-list-3-line' },
  { key: 'items', label: 'By Item', icon: 'ri-stack-line' },
  { key: 'stages', label: 'By Stage', icon: 'ri-git-merge-line' },
  { key: 'reasons', label: 'By Reason', icon: 'ri-question-line' },
  { key: 'recovery', label: 'Recovery (Sales)', icon: 'ri-hand-coin-line' },
];

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fyStart = () => {
  const now = new Date();
  return `${now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1}-04-01`;
};

const th = 'text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap';
const td = 'px-4 py-3 whitespace-nowrap';

/** Bar showing a group's share of the largest material cost */
const Share = ({ value, max }: { value: number; max: number }) => (
  <div className="w-24 h-1.5 rounded-full bg-[#f1f5f9] overflow-hidden">
    <div className="h-full bg-[#4f46e5]" style={{ width: `${max > 0 ? Math.max(2, (value / max) * 100) : 0}%` }} />
  </div>
);

export default function ScrapReportPage() {
  const navigate = useNavigate();
  const { selectedWarehouseId } = useWarehouseStore();
  const [from, setFrom] = useState(fyStart());
  const [to, setTo] = useState(iso(new Date()));
  const [tab, setTab] = useState<Tab>('summary');
  const [data, setData] = useState<ScrapReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await scrapApi.report({ from, to, warehouseId: selectedWarehouseId || undefined });
      setData(res.data);
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : 'Could not load the scrap report');
    } finally {
      setLoading(false);
    }
  }, [from, to, selectedWarehouseId]);

  useEffect(() => { void load(); }, [load]);

  const groups: ScrapGroup[] = useMemo(() => {
    if (!data) return [];
    return { summary: data.bySource, orders: data.byOrder, items: data.byItem, stages: data.byStage, reasons: data.byReason, recovery: [] }[tab];
  }, [data, tab]);
  const maxBook = Math.max(0, ...groups.map((g) => g.bookValue));

  const labelOf = (g: ScrapGroup) => {
    if (tab === 'summary') return SCRAP_SOURCE_LABEL[g.key as ScrapSource] || g.label;
    if (tab === 'reasons') return SCRAP_REASON_LABEL[g.key as ScrapReason] || g.label;
    return g.label;
  };

  const exportTab = () => {
    if (!data) return;
    const rows: Record<string, unknown>[] = tab === 'recovery'
      ? data.sales.map((s) => ({ 'Scrap No': s.scrapNumber, Date: dateText(s.disposalDate), Buyer: s.buyerName, Item: s.itemName, Qty: s.qty, Unit: s.unit, 'Held Value': s.scrapValue, 'Sale Amount': s.saleAmount, GST: s.gstAmount, 'Profit / Loss': s.result }))
      : groups.map((g) => ({
        [TABS.find((t) => t.key === tab)!.label]: labelOf(g),
        ...(tab === 'orders' ? { Product: g.productName, Planned: g.plannedQty, Good: g.goodQty, Rejected: g.rejectedQty, 'Yield %': g.yieldPct, 'Scrap %': g.scrapPct } : {}),
        ...(tab === 'items' ? { Qty: g.qty, Unit: g.unit } : {}),
        Entries: g.entries,
        'Material Cost': g.bookValue,
        'Scrap Value': g.scrapValue,
        'P&L Loss': g.loss,
        Recovered: g.recovery,
      }));
    exportToExcel(rows, Object.keys(rows[0] || {}).map((k) => ({ header: k, key: k })), `scrap-report-${tab}`, 'Scrap');
  };

  const t = data?.totals;

  return (
    <AppLayout>
      <div className="p-6 space-y-5 w-full">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => navigate('/reports')} className="w-9 h-9 flex items-center justify-center rounded-lg border border-[#e2e8f0] bg-white text-[#64748b] hover:bg-[#f8fafc] cursor-pointer">
              <i className="ri-arrow-left-line" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#1e293b]">Scrap Reports</h1>
              <p className="text-sm text-[#64748b] mt-0.5">Scrap by source, order, stage, item and reason — loss to the P&L and recovery from sales</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 px-2 rounded-lg border border-[#e2e8f0] text-sm bg-white" />
            <span className="text-xs text-[#94a3b8]">to</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 px-2 rounded-lg border border-[#e2e8f0] text-sm bg-white" />
            <button type="button" onClick={exportTab} disabled={!data || (tab === 'recovery' ? !data.sales.length : !groups.length)}
              className="h-9 px-3 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-[#475569] hover:bg-[#f8fafc] disabled:opacity-50 flex items-center gap-1.5 cursor-pointer">
              <i className="ri-file-excel-2-line" /> Export
            </button>
          </div>
        </div>

        {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>}

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Scrap Entries', value: t ? t.entries : '—', sub: 'Approved, disposed and pending' },
            { label: 'Material Cost Scrapped', value: t ? inr(t.bookValue) : '—', sub: `Scrap value ${t ? inr(t.scrapValue) : '—'}` },
            { label: 'Scrap Loss (P&L)', value: t ? inr(t.loss) : '—', sub: `Incl. disposal cost ${t ? inr(t.disposalCost) : '—'}`, cls: 'text-red-600' },
            { label: 'Recovered', value: t ? inr(t.recovery) : '—', sub: `GST on sales ${t ? inr(t.gstOnSales) : '—'}`, cls: 'text-green-700' },
            { label: 'Still to Dispose', value: t ? inr(t.pendingValue) : '—', sub: 'Pending + in scrap yard' },
          ].map((c) => (
            <div key={c.label} className="bg-white border border-[#e2e8f0] rounded-xl p-4">
              <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">{c.label}</p>
              <p className={`text-2xl font-bold mt-1 ${c.cls || 'text-[#1e293b]'}`}>{loading ? '…' : c.value}</p>
              <p className="text-[11px] text-[#94a3b8]">{c.sub}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-[#e2e8f0] overflow-x-auto">
          {TABS.map((x) => (
            <button key={x.key} type="button" onClick={() => setTab(x.key)}
              className={`h-10 px-4 flex items-center gap-1.5 text-sm font-medium border-b-2 whitespace-nowrap cursor-pointer ${tab === x.key ? 'border-[#4f46e5] text-[#4f46e5]' : 'border-transparent text-[#64748b] hover:text-[#1e293b]'}`}>
              <i className={x.icon} /> {x.label}
            </button>
          ))}
        </div>

        <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            {tab === 'recovery' ? (
              <table className="w-full min-w-max text-sm">
                <thead><tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                  {['Scrap No', 'Date', 'Buyer', 'Item', 'Qty', 'Held Value', 'Sale Amount', 'GST', 'Profit / Loss'].map((h) => <th key={h} className={th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {(data?.sales || []).map((s) => (
                    <tr key={s.id} className="border-b border-[#f1f5f9]">
                      <td className={`${td} font-mono text-xs text-[#4f46e5]`}>{s.scrapNumber}</td>
                      <td className={`${td} text-[#475569]`}>{dateText(s.disposalDate)}</td>
                      <td className={td}>{s.buyerName}</td>
                      <td className={td}>{s.itemName}</td>
                      <td className={td}>{qtyText(s.qty)} {s.unit}</td>
                      <td className={td}>{inr(s.scrapValue)}</td>
                      <td className={`${td} font-semibold`}>{inr(s.saleAmount)}</td>
                      <td className={`${td} text-[#64748b]`}>{inr(s.gstAmount)}</td>
                      <td className={`${td} font-semibold ${s.result >= 0 ? 'text-green-700' : 'text-red-600'}`}>{s.result >= 0 ? '+' : '−'}{inr(Math.abs(s.result))}</td>
                    </tr>
                  ))}
                  {!loading && !data?.sales.length && <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-[#94a3b8]">No scrap sold in this period</td></tr>}
                </tbody>
              </table>
            ) : (
              <table className="w-full min-w-max text-sm">
                <thead><tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                  <th className={th}>{TABS.find((x) => x.key === tab)!.label.replace('By ', '').replace('Summary', 'Source')}</th>
                  {tab === 'orders' && ['Good', 'Rejected', 'Yield', 'Scrap %'].map((h) => <th key={h} className={th}>{h}</th>)}
                  {tab === 'items' && <th className={th}>Qty</th>}
                  {['Entries', 'Material Cost', '', 'Scrap Value', 'P&L Loss', 'Recovered'].map((h, i) => <th key={i} className={th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {groups.map((g) => (
                    <tr key={g.key} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc]">
                      <td className={td}>
                        {tab === 'orders' ? (
                          <button type="button" onClick={() => navigate(`/manufacturing/production-orders/${g.key}`)} className="text-left cursor-pointer">
                            <span className="font-mono text-xs font-semibold text-[#4f46e5] hover:underline">{g.label}</span>
                            {g.productName && <span className="block text-[11px] text-[#94a3b8]">{g.productName}</span>}
                          </button>
                        ) : (
                          <span className="font-medium text-[#1e293b]">{labelOf(g)}{g.itemCode ? <span className="ml-1 font-mono text-[11px] text-[#94a3b8]">{g.itemCode}</span> : null}</span>
                        )}
                      </td>
                      {tab === 'orders' && (
                        <>
                          <td className={td}>{qtyText(g.goodQty)}</td>
                          <td className={`${td} text-red-600`}>{qtyText(g.rejectedQty)}</td>
                          <td className={td}>{g.yieldPct != null ? `${g.yieldPct}%` : '—'}</td>
                          <td className={`${td} font-semibold ${Number(g.scrapPct) > 5 ? 'text-red-600' : 'text-[#1e293b]'}`}>{g.scrapPct != null ? `${g.scrapPct}%` : '—'}</td>
                        </>
                      )}
                      {tab === 'items' && <td className={td}>{qtyText(g.qty)} <span className="text-xs text-[#94a3b8]">{g.unit}</span></td>}
                      <td className={td}>{g.entries}</td>
                      <td className={`${td} font-semibold`}>{inr(g.bookValue)}</td>
                      <td className={td}><Share value={g.bookValue} max={maxBook} /></td>
                      <td className={td}>{inr(g.scrapValue)}</td>
                      <td className={`${td} text-red-600`}>{inr(g.loss)}</td>
                      <td className={`${td} text-green-700`}>{inr(g.recovery)}</td>
                    </tr>
                  ))}
                  {!loading && !groups.length && (
                    <tr><td colSpan={12} className="px-4 py-10 text-center text-sm text-[#94a3b8]">No scrap in this period</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {tab === 'summary' && data && data.byMonth.length > 0 && (
          <div className="bg-white border border-[#e2e8f0] rounded-xl p-4">
            <h3 className="text-sm font-semibold text-[#1e293b] mb-3">Month by month</h3>
            <div className="space-y-2">
              {data.byMonth.slice().sort((a, b) => a.key.localeCompare(b.key)).map((m) => {
                const max = Math.max(...data.byMonth.map((x) => x.bookValue));
                return (
                  <div key={m.key} className="flex items-center gap-3 text-xs">
                    <span className="w-20 text-[#64748b]">{new Date(`${m.key}-01`).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}</span>
                    <div className="flex-1 h-2 rounded-full bg-[#f1f5f9] overflow-hidden">
                      <div className="h-full bg-[#4f46e5]" style={{ width: `${max > 0 ? Math.max(2, (m.bookValue / max) * 100) : 0}%` }} />
                    </div>
                    <span className="w-28 text-right font-semibold text-[#1e293b]">{inr(m.bookValue)}</span>
                    <span className="w-28 text-right text-red-600">loss {inr(m.loss)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
